<template>
  <form ref="paymentSelectionForm" @submit.prevent="submitStep4">
    <div class="grid gap-24">
      <CheckoutStageComponentsContainer>
        <h2 class="heading-xs mb-16">Välj betalsätt</h2>
        <div v-if="isLoadingPaymentMethodsForTheFirstTime">
          <RoundedLoadingBox message="Hämtar betalsätt" />
        </div>

        <CheckoutSelectPaymentOption
          :radio-buttons-group-name="PAYMENT_METHOD_RADIO_BUTTONS"
          :payment-methods="paymentMethods"
        />
        <div aria-live="polite" aria-atomic>
          <AppErrorText
            v-if="
              !isLoadingPaymentMethodsForTheFirstTime &&
              errors[PAYMENT_METHOD_RADIO_BUTTONS]
            "
            class="mt-16"
          >
            {{ errors[PAYMENT_METHOD_RADIO_BUTTONS] }}
          </AppErrorText>
        </div>
      </CheckoutStageComponentsContainer>

      <CheckoutStageComponentsContainer v-if="shouldSignItemsInCart()">
        <div>
          <p class="max-w-546">
            När jag trycker på "Slutför köp" godkänner jag
            <NuxtLink
              to="https://www.halebop.se/villkor-och-prislistor"
              target="_blank"
            >
              villkor
            </NuxtLink>
            och att Halebop (en del av Telia Sverige) gör en kreditkontroll.
          </p>
        </div>
        <div
          v-if="store.cartContainsCellphone"
          class="mt-16 border-t-md border-border-secondary"
        >
          <p class="max-w-546 mt-16">
            Halebop Mobilförsäkring kan tecknas på bekräftelsesidan direkt efter
            att du slutfört ditt köp. Den första månaden bjuder vi på, därefter
            kostar det 149 kr/mån.
          </p>
        </div>
      </CheckoutStageComponentsContainer>

      <GlobalNotificationContainer
        :show="!!notificationStore.title"
        :is-just-text-row="true"
      />

      <ContractSummary :generate-on-mount="true" />

      <template
        v-if="
          store.userState.payment.selectedPaymentMethodSystemName !==
            TELIAPAY_METHOD &&
          store.userState.payment.selectedPaymentMethodSystemName !==
            KLARNA_METHOD
        "
      >
        <AppButton
          v-if="!shouldSignItemsInCart()"
          full-width
          class="max-w-400 m-auto w-full"
          :show-loading="isSubmitting"
          :disabled="store.isStep4ProceedBlocked"
        >
          Slutför köp
        </AppButton>
        <AppButton
          v-else
          full-width
          class="max-w-400 m-auto w-full"
          :show-loading="isSubmitting"
          button-type="button"
          :disabled="store.isStep4ProceedBlocked"
          @on-click="openBankIdModal()"
        >
          Slutför köp
          <nuxt-icon name="bankId" aria-label="bankid ikon" />
        </AppButton>

        <AppButton
          v-if="isTestEnvironment() && shouldSignItemsInCart()"
          full-width
          color="secondary"
          class="max-w-400 m-auto w-full"
          :show-loading="isSubmitting"
          :disabled="store.isStep4ProceedBlocked"
        >
          Slutför köp test utan bankid
        </AppButton>
      </template>
      <template
        v-if="
          store.userState.payment.selectedPaymentMethodSystemName ===
          KLARNA_METHOD
        "
      >
        <div>
          <AppButton
            full-width
            class="max-w-400 m-auto w-full mb-16"
            :show-loading="isSubmitting"
            button-type="button"
            :disabled="store.isStep4ProceedBlocked"
            @on-click="payWithKlarna"
          >
            Betala med Klarna
          </AppButton>
          <LazyPaymentLogosHorizontal centered />
        </div>
      </template>
    </div>
  </form>

  <AppModal
    v-model="showBankIdSignModal"
    dialog-titel="Identifiera dig med bankid"
  >
    <div class="flex justify-center">
      <NuxtImg
        src="/images/logos/mobiltbankid_logo.png"
        alt="BankId logo"
        loading="lazy"
        provider="cloudfront"
      />
    </div>
    <AppBankID
      :is-sign="true"
      :open-immediately="true"
      @on-complete="OnBankIdSigned"
    />
  </AppModal>
</template>

<script lang="ts" setup>
import {
  KLARNA_METHOD,
  TELIAPAY_METHOD,
} from '~/models/checkout/PaymentMethods.model';
import { Category } from '~/models/Category.models';
import {
  type Step4FormValues,
  PAYMENT_METHOD_RADIO_BUTTONS,
  SWISH_NUMBER,
} from '~/models/checkout/forms/Step4Form.model';
import type { HalebopRequestErrorData } from '~/repository/factory';
import { useKlarna } from '~/composables/checkout/useKlarna';
import type { KlarnaAuthorizationCallbackResponse } from '~/models/checkout/KlarnaSDKInterfaces';
import { useFetchPaymentMethods } from '~/composables/checkout/useFetchPaymentMethods';
import { isTestEnvironment } from '~/utils/general/isTestEnvironment';
import { useCreditCheck } from '~/composables/checkout/useCreditCheck';
import ContractSummary from '../ContractSummary.vue';
import { checkout_sendCheckoutLogToBackend } from '~/utils/checkout/sendCheckoutLogToBackend';

const { $api } = useNuxtApp();
const { $validator } = useNuxtApp();
const store = useMyCheckoutStore();
const notificationStore = useNotificationStore();
const { pushStartPaymentEvent } = useGoogleAnalyticsEvents();
const { authorizeKlarna, onShowFormFalse } = useKlarna();
const { performCreditCheck } = useCreditCheck();

const step4FormValidationSchema: unknown = {
  [PAYMENT_METHOD_RADIO_BUTTONS]: $validator.paymentOptionStep4IsSelected,
  [SWISH_NUMBER]: [
    $validator.swishStep4NumberNotEmpty,
    $validator.swishStep4NumberCorrectFormat,
  ],
};

const {
  paymentMethods,
  isLoadingPaymentMethodsForTheFirstTime,
  klarnaSessionId,
} = useFetchPaymentMethods();

const { errors, handleSubmit, isSubmitting, validate } =
  useForm<Step4FormValues>({
    validationSchema: step4FormValidationSchema,
    initialValues: {
      [PAYMENT_METHOD_RADIO_BUTTONS]: undefined,
      [SWISH_NUMBER]: undefined,
    },
    validateOnMount: false,
  });

const showBankIdSignModal = ref(false);
const paymentSelectionForm = ref();

function shouldSignItemsInCart() {
  const items = store.$state.fetchedData.cart.cartItems;
  const onlyAccessoriesOrPrePaid = items.every(
    (item) => item.Category === Category.accessory,
  );
  return !onlyAccessoriesOrPrePaid;
}

function OnBankIdSigned() {
  proceedToPayment();
}

function proceedToPayment() {
  showBankIdSignModal.value = false;
  paymentSelectionForm.value.requestSubmit();
}

function openBankIdModal() {
  validate().then((result) => {
    if (result.valid) {
      if (shouldSignItemsInCart()) {
        showBankIdSignModal.value = true;
      } else {
        proceedToPayment();
      }
    }
  });
}

const pushPostCheckoutAnalyticsEvents = () => {
  pushStartPaymentEvent(store.trackingData.selectedPayment);
};

const payWithKlarna = async () => {
  const shouldProceed = await performCreditCheck();

  checkout_sendCheckoutLogToBackend(
    'Shop',
    'Post Credit Check (Klarna)',
    `Checkout should proceed from check: ${shouldProceed}. Cart item ids: ${store.fetchedData.cart.cartItems.flatMap((x) => x.Id).join(', ')}`,
  );

  if (!shouldProceed) {
    return;
  }

  authorizeKlarna(async (res: KlarnaAuthorizationCallbackResponse) => {
    if (res.approved === true && res.authorization_token) {
      const result = await $api.klarnaModule.savePaymentAuthorization(
        res.authorization_token,
        klarnaSessionId.value || '',
      );

      if (result) {
        notificationStore.setStandardErrorNotification();
      }

      checkout_sendCheckoutLogToBackend(
        'Shop',
        'Klarna authorize approved',
        `Approved by klarna. KlarnaSessionId: ${klarnaSessionId?.value}`,
      );

      submitStep4();
    } else if (res.approved === false && res.show_form === false) {
      onShowFormFalse();
    }
  });
};

const submitStep4 = handleSubmit(async () => {
  notificationStore.reset();

  if (
    store.userState.payment.selectedPaymentMethodSystemName !== KLARNA_METHOD
  ) {
    const shouldProceed = await performCreditCheck();

    checkout_sendCheckoutLogToBackend(
      'Shop',
      'Submit Step 4 credit check',
      `Checkout should proceed from check: ${shouldProceed}. Cart item ids: ${store.fetchedData.cart.cartItems.flatMap((x) => x.Id).join(', ')}`,
    );

    if (!shouldProceed) {
      return;
    }
  }

  try {
    const redirectUrl = await store.finalizeCheckout();

    pushPostCheckoutAnalyticsEvents();

    cookies_clearAccessToken();

    if (redirectUrl) {
      navigateTo(redirectUrl, { external: true });
    }
  } catch (error) {
    console.log(error);
    if (
      isNuxtError<HalebopRequestErrorData>(error) &&
      error.data &&
      error.data.errors &&
      Object.keys(error.data.errors).length > 0
    ) {
      let validationErrorsString = Object.values(error.data.errors)
        .flatMap(
          (errorStringArrayForAField: unknown) =>
            errorStringArrayForAField as string,
        )
        .map((errorString: string) => {
          return errorString.endsWith('.')
            ? errorString.slice(0, -1)
            : errorString;
        })
        .join(', ');

      if (!validationErrorsString) {
        validationErrorsString =
          'Du har fyllt i värden som behöver rättas till';
      }

      const message = `Beställningen kan inte läggas: ${validationErrorsString}. Vänligen gå tillbaka och ändra det du har fyllt i.`;

      notificationStore.setErrorNotificationFromCatchClause(
        message,
        'Beställningen kunde inte genomföras',
      );

      return;
    }

    if (isNuxtError<HalebopRequestErrorData>(error) && error.message) {
      logError(error);

      notificationStore.setErrorNotificationFromCatchClause(
        error.message,
        'Kunde inte slutföra köp, försök igen senare.',
      );

      return;
    }

    if (
      isNuxtError<HalebopRequestErrorData>(error) &&
      error.data?.title === 'User error' &&
      error.data?.detail
    ) {
      logError(error);

      notificationStore.setErrorNotificationFromCatchClause(
        error.data?.detail,
        'Kunde inte slutföra köp, försök igen senare.',
      );

      return;
    }

    notificationStore.setErrorNotificationFromCatchClause(
      undefined,
      'Kunde inte slutföra köp, försök igen senare.',
    );
  }
});
</script>

<style lang="scss" scoped></style>
